import { Maybe, Label, UserType } from '../../../__generated__/globalTypes'

import {
  UserSettingsFragment,
  isUserSettingsFragment,
} from './UserSettingsFragment'

import { AddressFragment, isAddressFragment } from './AddressFragment'

import { Storefront, isStorefront } from './Storefront'

import {
  MinimalSponsorFragment,
  isMinimalSponsorFragment,
} from './MinimalSponsorFragment'

import { StripeSubscription, isStripeSubscription } from './StripeSubscription'

import { ExpenseReload, isExpenseReload } from './ExpenseReload'

import { Amount, isAmount } from './Amount'

import {
  StripeSourceFragment,
  isStripeSourceFragment,
} from './StripeSourceFragment'

import { PlanFragment, isPlanFragment } from './PlanFragment'

import accountFragmentRawString from '../AccountFragment.graphql'

export const accountFragmentString = accountFragmentRawString

export type AccountFragment = {
  id: string
  username: string
  firstName: string
  lastName: string
  email: string
  profileImageUrl: Maybe<string>
  isStaff: boolean
  phoneNumber: string
  isAffiliate: boolean
  isNewUser: boolean
  createdAt: string
  genealogyId: number
  stripeCustomerId: Maybe<string>
  groupSends: number
  loyaltyRewards: number
  scheduledSends: number
  systemSends: number
  heartfeltSends: number
  pointPrice: number
  promptingProLoginUrl: string
  promptinguLoginUrl: Maybe<string>
  streamMarketingLoginUrl: Maybe<string>
  promptinguJwt: string
  token: string
  couldQualifyForFirstFreeCard: boolean
  isQualifiedForFirstFreeCard: boolean
  isTrialAffiliate: boolean
  labels: Label[]
  type: UserType
  settings: Maybe<UserSettingsFragment>
  shippingAddress: Maybe<AddressFragment>
  sponsor: Maybe<MinimalSponsorFragment>
  storefront: Maybe<Storefront>
  plan: PlanFragment
  stripeSource: Maybe<StripeSourceFragment>
  stripeSubscription: Maybe<StripeSubscription>
  subscriptions: StripeSubscription[]
  points: Amount
  expense: Amount
  expenseReload: Maybe<ExpenseReload>
  verifiedPhoneNumber: Maybe<{
    nationalNumber: string
  }>
}

export const isAccountFragment = (fragment: any): fragment is AccountFragment =>
  fragment &&
  typeof fragment.id === 'string' &&
  typeof fragment.username === 'string' &&
  typeof fragment.firstName === 'string' &&
  typeof fragment.lastName === 'string' &&
  typeof fragment.email === 'string' &&
  (fragment.profileImageUrl === null ||
    typeof fragment.profileImageUrl === 'string') &&
  typeof fragment.isStaff === 'boolean' &&
  typeof fragment.phoneNumber === 'string' &&
  typeof fragment.isAffiliate === 'boolean' &&
  typeof fragment.isNewUser === 'boolean' &&
  typeof fragment.createdAt === 'string' &&
  typeof fragment.genealogyId === 'number' &&
  (fragment.stripeCustomerId === null ||
    typeof fragment.stripeCustomerId === 'string') &&
  typeof fragment.groupSends === 'number' &&
  typeof fragment.loyaltyRewards === 'number' &&
  typeof fragment.scheduledSends === 'number' &&
  typeof fragment.systemSends === 'number' &&
  typeof fragment.heartfeltSends === 'number' &&
  typeof fragment.pointPrice === 'number' &&
  typeof fragment.promptingProLoginUrl === 'string' &&
  (fragment.promptinguLoginUrl === null ||
    typeof fragment.promptinguLoginUrl === 'string') &&
  (fragment.streamMarketingLoginUrl === null ||
    typeof fragment.streamMarketingLoginUrl === 'string') &&
  typeof fragment.promptinguJwt === 'string' &&
  typeof fragment.token === 'string' &&
  typeof fragment.couldQualifyForFirstFreeCard === 'boolean' &&
  typeof fragment.isQualifiedForFirstFreeCard === 'boolean' &&
  typeof fragment.isTrialAffiliate === 'boolean' &&
  Array.isArray(fragment.labels) &&
  fragment.labels
    .slice(0, 5)
    .reduce(
      (accum: any, next: any) =>
        accum &&
        (next === 'ZERO_POINT' ||
          next === 'AFFILIATE' ||
          next === 'STAFF' ||
          next === 'PRODUCT_LAUNCH' ||
          next === 'GIFTING_COMMITTEE' ||
          next === 'NOWSITE_ACTIVE' ||
          next === 'NOWSITE_NEED_SETUP' ||
          next === 'AFFILIATE_MISSING_W9' ||
          next === 'AFFILIATE_MISSING_W8' ||
          next === 'AFFILIATE_RECEIVES_PHYSICAL_CHECKS' ||
          next === 'NEEDS_GS_AFFILIATE_OPT_IN' ||
          next === 'NEEDS_CUSTOMER_REFERRAL_OPT_IN' ||
          next === 'SHOULD_HIDE_REVOLUTION_OFFICE'),
      true,
    ) &&
  (fragment.type === 'SP123' ||
    fragment.type === 'S' ||
    fragment.type === 'C' ||
    fragment.type === 'RC' ||
    fragment.type === 'D' ||
    fragment.type === 'GSA' ||
    fragment.type === 'SA' ||
    fragment.type === 'BBC' ||
    fragment.type === 'BBD' ||
    fragment.type === 'MBC' ||
    fragment.type === 'MBD' ||
    fragment.type === 'PBC' ||
    fragment.type === 'PBD' ||
    fragment.type === 'JO' ||
    fragment.type === 'ID' ||
    fragment.type === 'MD' ||
    fragment.type === 'WCS' ||
    fragment.type === 'WCG') &&
  (fragment.settings === null ||
    (isUserSettingsFragment(fragment.settings) as boolean)) &&
  (fragment.shippingAddress === null ||
    (isAddressFragment(fragment.shippingAddress) as boolean)) &&
  (fragment.sponsor === null ||
    (isMinimalSponsorFragment(fragment.sponsor) as boolean)) &&
  (fragment.storefront === null ||
    (isStorefront(fragment.storefront) as boolean)) &&
  (isPlanFragment(fragment.plan) as boolean) &&
  (fragment.stripeSource === null ||
    (isStripeSourceFragment(fragment.stripeSource) as boolean)) &&
  (fragment.stripeSubscription === null ||
    (isStripeSubscription(fragment.stripeSubscription) as boolean)) &&
  Array.isArray(fragment.subscriptions) &&
  fragment.subscriptions
    .slice(0, 5)
    .reduce(
      (accum: any, next: any) =>
        accum && (isStripeSubscription(next) as boolean),
      true,
    ) &&
  (isAmount(fragment.points) as boolean) &&
  (isAmount(fragment.expense) as boolean) &&
  (fragment.expenseReload === null ||
    (isExpenseReload(fragment.expenseReload) as boolean)) &&
  (fragment.verifiedPhoneNumber === null ||
    (fragment.verifiedPhoneNumber &&
      typeof fragment.verifiedPhoneNumber.nationalNumber === 'string'))
